import React from 'react';
import { Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import jwt_decode from 'jwt-decode';

import ErrorBoundary from '@/ErrorBoundary';

import { initRefresh } from '@/helpers/refresh';

import AuthProvider, { AuthConsumer, TAuthRetrieveUserCallback } from '@/providers/AuthProvider';
import { CampaignProvider } from '@/providers/AuthProvider/campaignInfoProvider';
import { LaboratoryProvider } from '@/providers/AuthProvider/labInfoProvider';
import { PharmacyProvider } from '@/providers/AuthProvider/pharmacyInfoProvider';
import LocaleProvider from '@/providers/LocaleProvider';

import translations from '@/translations';

/**
 * Routers
 */
import NotAuthRouter from '../NotAuthRouter';
import AuthRouter from '../AuthRouter';

/**
 * Get router's history
 */
export const history = createBrowserHistory();

/**
 * @param token stored on cookie allow re-authentification.
 * @returns User's informations store on authentification.
 */
const onRetrieveUserFromCookie: TAuthRetrieveUserCallback = (token) => Promise.resolve(jwt_decode(token));

const AppRouter: React.FC = () => {
  const [{ action, location }, setState] = React.useState({
    action: history.action,
    location: history.location,
  });

  React.useEffect(() => {
    window.history.replaceState({}, document.title);
  }, [location]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useLayoutEffect(() => history.listen(setState), [history]);

  React.useEffect(() => {
    initRefresh();
  }, []);

  return (
    <Router location={location} navigationType={action} navigator={history}>
      <CampaignProvider>
        <LaboratoryProvider>
          <PharmacyProvider>
            <AuthProvider onRetrieveUserFromCookie={onRetrieveUserFromCookie}>
              <LocaleProvider translations={translations}>
                <ErrorBoundary>
                  <AuthConsumer>{({ user }) => (user === null ? <NotAuthRouter /> : <AuthRouter />)}</AuthConsumer>
                </ErrorBoundary>
              </LocaleProvider>
            </AuthProvider>
          </PharmacyProvider>
        </LaboratoryProvider>
      </CampaignProvider>
    </Router>
  );
};

export default AppRouter;
